import { useNavigate } from "react-router-dom";
import { useDownload } from "../../../hooks/useDownload.ts";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { HOME, PURCHASES, WORKSPACES } from "../../../routes.ts";
import { notifier } from "../../../utils/notifier.tsx";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";
import { RESOURCE_TYPE_TO_EXTENSION } from "../constants.ts";
import type { DownloadContent } from "../types.ts";
import { useBoardUuid } from "./useBoardUuid.ts";
import { useCompileEditionCanvas } from "./useCompileEditionCanvas.ts";

export const usePurchaseAndDownload = ({
  assetUuids,
  contentUuids,
}: {
  assetUuids: string[];
  contentUuids: string[];
}) => {
  const workspaceUuid = useWorkspaceUuid();
  const boardUuid = useBoardUuid();
  const { mutate: download, isLoading: isDownloading } = useDownload();
  const onBeforeDownload = useCompileEditionCanvas();
  const navigate = useNavigate();
  const usePurchaseMutation = useAppMutation({
    path: "contents/purchase",
    invalidate: [
      ...assetUuids.map((assetUuid) => `assets/${assetUuid}`),
      `boards/${boardUuid}/assets`,
      `workspaces/${workspaceUuid}`,
    ],
    onSuccess: (response: { data: DownloadContent[] }) => {
      notifier.success("Watermark removed successfully!", {
        message: "See in purchased images",
        onClick: () => {
          navigate(`${WORKSPACES}/${workspaceUuid}/${HOME}/${PURCHASES}`);
        },
      });

      download({
        items: response.data.map((item) => ({
          url: item.download_url,
          filename: `${item.uuid}.${
            RESOURCE_TYPE_TO_EXTENSION[item.resource_type]
          }`,
          type: item.resource_type,
        })),
        onBeforeDownload,
        onError: () => {
          notifier.error("Download failed");
        },
      });
    },
  }).mutation;

  return {
    mutate: () => {
      usePurchaseMutation.mutate({
        uuids: contentUuids,
        workspace_uuid: workspaceUuid,
      });
    },
    isLoading: usePurchaseMutation.isLoading || isDownloading,
  };
};
